import { NEW_LANDINGI_URL } from '@config/env'
import { ANNUALLY, BillingPeriodType, MONTHLY } from '@constants/billingPeriods'
import { CurrencyType } from '@constants/currencies'
import { FREE_PLANS, PLANS } from '@constants/plans'
import { useUserContext } from '@contexts/user'
import NiceModal from '@ebay/nice-modal-react'
import {
  arePlansEqualPrice,
  isPlanCheaper,
  isPlanMoreExpensive
} from '@helpers/plans'
import { contactSupport } from '@helpers/support'
import {
  Button,
  CustomLink,
  Icon,
  Paragraph,
  Spreader
} from '@landingi/landingi-ui-kit'
import { MODAL_SUMMARY_SUBSCRIPTION } from '@pages/Pricing/routes/AccountPackage/components/PlanPanels/components/SummarySubscriptionModal'
import { useAccountPackageContext } from '@pages/Pricing/routes/AccountPackage/contexts'
import { Trans, useTranslation } from 'react-i18next'
import { Column, Row } from 'simple-flexbox'

import styles from './planCTAs.module.scss'

export const DOWNGRADE = 'DOWNGRADE'
export const UPGRADE = 'UPGRADE'
export const CURRENT_MONTHLY = 'CURRENT_MONTHLY'
export const CURRENT_ANNUALY = 'CURRENT_ANNUALLY'
export const CHANGE_BILLING_CYCLE = 'CHANGE_BILLING_CYCLE'
export const CHOOSE_PLAN = 'CHOOSE_PLAN'
export const CONTACT_US = 'CONTACT_US'
export const FREE_PLAN_CURRENT = 'FREE_PLAN_CURRENT'
export const RENEW = 'RENEW'

const { LITE_DISC_22, PROFESSIONAL_DISC_22, FREE_22 } = PLANS

const DowngradeButton = ({
  isDisabled,
  planRecordKey
}: {
  isDisabled?: boolean
  planRecordKey?: string
}) => {
  const { t } = useTranslation()

  return (
    <Button
      variant='secondary-outlined'
      isDisabled={isDisabled}
      size='large'
      onClick={() =>
        NiceModal.show(MODAL_SUMMARY_SUBSCRIPTION, {
          planRecordKey,
          buttonType: DOWNGRADE
        })
      }
    >
      {t('change.plan.downgrade')}
    </Button>
  )
}

const UpgradeButton = ({
  isDisabled,
  planRecordKey
}: {
  isDisabled?: boolean
  planRecordKey?: string
}) => {
  const { t } = useTranslation()

  return (
    <Button
      isDisabled={isDisabled}
      size='large'
      onClick={() =>
        NiceModal.show(MODAL_SUMMARY_SUBSCRIPTION, {
          planRecordKey,
          buttonType: UPGRADE
        })
      }
    >
      {t('change.plan.page.upgrade')}
    </Button>
  )
}

const ChoosePlanButton = ({
  isDisabled,
  planRecordKey
}: {
  isDisabled?: boolean
  planRecordKey?: string
}) => {
  const { t } = useTranslation()

  return (
    <Button
      isDisabled={isDisabled}
      size='large'
      onClick={() =>
        NiceModal.show(MODAL_SUMMARY_SUBSCRIPTION, {
          planRecordKey
        })
      }
    >
      {t('change.plan.page.choose.plan')}
    </Button>
  )
}

const RenewButton = ({ isDisabled }: { isDisabled?: boolean }) => {
  const { t } = useTranslation()
  const { hasSubscription } = useUserContext()

  const onClick = () => {
    if (hasSubscription) {
      window.open(`https://${NEW_LANDINGI_URL}/payments/subscription`, '_self')
    } else {
      window.open(`https://${NEW_LANDINGI_URL}/payments`, '_self')
    }
  }

  return (
    <Button isDisabled={isDisabled} size='large' onClick={onClick}>
      {t('change.plan.page.renew')}
    </Button>
  )
}

const CurrentPlanMonthly = ({
  saving,
  currency,
  isDisabled
}: {
  saving?: number | string
  currency?: CurrencyType
  isDisabled?: boolean
}) => {
  const { t } = useTranslation()
  const { setChosenBillingPeriod } = useAccountPackageContext()
  const setChosenBillingPeriodTo12 = () => setChosenBillingPeriod(12)

  return (
    <Row>
      <Column justifyContent='center'>
        <Icon icon='icon-check-circle' size={20} />
      </Column>

      <Spreader spread='tiny' />

      <Column>
        <Paragraph padding='none' line={22}>
          <Trans i18nKey='change.plan.page.current.plan.billed.monthly' />
        </Paragraph>

        <CustomLink
          size={12}
          label={t('change.plan.page.save.change.billing.cycle', {
            saving,
            currency
          })}
          underlined
          variant={isDisabled ? 'inactive' : 'active'}
          onClick={setChosenBillingPeriodTo12}
          className={styles.link}
        />
      </Column>
    </Row>
  )
}

const CurrentPlanAnnually = () => (
  <Row>
    <Column justifyContent='center'>
      <Icon icon='icon-check-circle' size={20} />
    </Column>

    <Spreader spread='tiny' />

    <Paragraph padding='none' line={22}>
      <Trans i18nKey='change.plan.page.current.plan.billed.annually' />
    </Paragraph>
  </Row>
)

const ChangeBillingCycle = ({
  isDisabled,
  planRecordKey
}: {
  isDisabled?: boolean
  planRecordKey?: string
}) => {
  const { t } = useTranslation()

  return (
    <Button
      isDisabled={isDisabled}
      size='large'
      onClick={() =>
        NiceModal.show(MODAL_SUMMARY_SUBSCRIPTION, {
          planRecordKey,
          buttonType: CHANGE_BILLING_CYCLE
        })
      }
    >
      {t('change.plan.page.change.billing.cycle.button')}
    </Button>
  )
}

const handleContactSupport = () => contactSupport()

const ContactUs = ({ isDisabled }: { isDisabled?: boolean }) => {
  const { t } = useTranslation()

  return (
    <Button
      variant='secondary-outlined'
      isDisabled={isDisabled}
      size='large'
      onClick={handleContactSupport}
    >
      {t('button.contact.us')}
    </Button>
  )
}

export const planCTAs = {
  [DOWNGRADE]: DowngradeButton,
  [UPGRADE]: UpgradeButton,
  [CURRENT_MONTHLY]: CurrentPlanMonthly,
  [CURRENT_ANNUALY]: CurrentPlanAnnually,
  [RENEW]: RenewButton,
  [CHANGE_BILLING_CYCLE]: ChangeBillingCycle,
  [CHOOSE_PLAN]: ChoosePlanButton,
  [CONTACT_US]: ContactUs,
  [FREE_PLAN_CURRENT]: null
}

export type PlanCTAsType = typeof planCTAs

const getCurrentPlanCTAName = ({
  hasSubscription,
  billingPeriod,
  currentBillingPeriod
}: {
  hasSubscription?: boolean
  billingPeriod?: BillingPeriodType
  currentBillingPeriod?: BillingPeriodType
}) => {
  if (
    hasSubscription &&
    currentBillingPeriod === billingPeriod &&
    billingPeriod === MONTHLY
  ) {
    return CURRENT_MONTHLY
  }

  if (
    hasSubscription &&
    currentBillingPeriod === billingPeriod &&
    billingPeriod === ANNUALLY
  ) {
    return CURRENT_ANNUALY
  }

  if (hasSubscription && currentBillingPeriod !== billingPeriod) {
    return CHANGE_BILLING_CYCLE
  }

  return RENEW
}

export const getPlanCTAName = ({
  hasSubscription,
  planRecordKey,
  currentPlanRecordKey,
  billingPeriod,
  currentBillingPeriod,
  isOldPlan
}: {
  hasSubscription?: boolean
  planRecordKey?: string
  currentPlanRecordKey?: string
  billingPeriod?: BillingPeriodType
  currentBillingPeriod?: BillingPeriodType
  isOldPlan?: boolean
}) => {
  const discountedPlans: string[] = [LITE_DISC_22, PROFESSIONAL_DISC_22]

  if (
    FREE_PLANS.includes(currentPlanRecordKey as (typeof FREE_PLANS)[number]) &&
    planRecordKey === FREE_22
  ) {
    return FREE_PLAN_CURRENT
  }

  if (currentPlanRecordKey !== FREE_22 && planRecordKey === FREE_22) {
    return CONTACT_US
  }

  if (discountedPlans.includes(currentPlanRecordKey as string)) {
    return CHOOSE_PLAN
  }

  if (
    arePlansEqualPrice({
      plan1: planRecordKey,
      plan2: currentPlanRecordKey,
      isOldPlan
    })
  ) {
    return getCurrentPlanCTAName({
      hasSubscription,
      billingPeriod,
      currentBillingPeriod
    })
  }

  if (
    isPlanCheaper({
      plan1: planRecordKey,
      plan2: currentPlanRecordKey,
      isOldPlan
    })
  ) {
    return DOWNGRADE
  }

  if (
    isPlanMoreExpensive({
      plan1: planRecordKey,
      plan2: currentPlanRecordKey,
      isOldPlan
    })
  ) {
    return UPGRADE
  }

  return CONTACT_US
}

export const useGetPlanCTA = ({
  planRecordKey,
  isOldPlan
}: {
  planRecordKey: string
  isOldPlan?: boolean
}) => {
  const {
    hasSubscription,
    planRecordKey: currentPlanRecordKey,
    billingPeriod
  } = useUserContext()
  const { chosenBillingPeriod } = useAccountPackageContext()

  return planCTAs[
    getPlanCTAName({
      hasSubscription,
      planRecordKey,
      currentPlanRecordKey,
      currentBillingPeriod: billingPeriod,
      billingPeriod: chosenBillingPeriod,
      isOldPlan
    })
  ]
}
